import { useState } from "react";
import { Navigate, Outlet, useLocation, useMatches, useNavigate } from "react-router-dom";
import { AppShell } from "../../components";
import type { AppShellNavSection } from "../../components";
import { useLogout, useMe } from "../../api/auth";
import { ChangePasswordModal } from "./ChangePasswordModal";

/** Route `handle` shape used for per-route topbar titles. */
export interface RouteHandle {
  title?: string;
}

const NAV: AppShellNavSection[] = [
  {
    section: "",
    items: [
      { to: "/about", label: "About" },
      { to: "/", label: "Dashboard", icon: "DB" },
    ],
  },
  { section: "Pricing", items: [{ to: "/projects", label: "Projects" }] },
  {
    section: "Catalogue",
    items: [
      { to: "/suppliers", label: "Suppliers" },
      { to: "/brands", label: "Brands" },
      { to: "/rate-cards", label: "Rate cards" },
      { to: "/discounts", label: "Discounts" },
      { to: "/products", label: "Products" },
    ],
  },
  {
    section: "Data",
    items: [
      { to: "/price-lists", label: "Price lists" },
      { to: "/comparables", label: "Comparables" },
    ],
  },
  {
    section: "System",
    items: [
      { to: "/activity", label: "Activity" },
      { to: "/ai-usage", label: "AI usage" },
      { to: "/users", label: "Users" },
    ],
  },
];

/**
 * Auth gate + chrome for every screen except /login: renders nothing while the
 * session check is in flight, bounces to /login when unauthenticated, and
 * otherwise wraps the matched route in the AppShell.
 */
export function ProtectedLayout() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const matches = useMatches();
  const [changingPassword, setChangingPassword] = useState(false);

  if (me.isPending) return null;
  if (!me.data) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const title =
    [...matches]
      .reverse()
      .map((match) => (match.handle as RouteHandle | undefined)?.title)
      .find((candidate) => candidate !== undefined) ?? "Reina Group";

  return (
    <AppShell
      nav={NAV}
      title={title}
      user={{ name: me.data.name }}
      onLogout={() =>
        logout.mutate(undefined, {
          onSettled: () => navigate("/login", { replace: true }),
        })
      }
      onChangePassword={() => setChangingPassword(true)}
    >
      <Outlet />
      {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}
    </AppShell>
  );
}
